<script setup lang="ts">
import { actionTransformer, actionModalTransformer } from './actionHelpers'
import { Variants, ButtonProps } from '~/components/ui/Button.vue'
import { AnchorProps } from '~/components/ui/Anchor.vue'
import { IconFields } from '../Media/Icon.vue'
import { PictureFields } from '../Media/Picture.vue'

defineOptions({
  inheritAttrs: false,
})

export type Type = 'Button' | 'Link'
export type ActionType = 'addToBag' | 'setMyStore' | 'mhj-login-panel' | 'mhj-signup-panel'
export type ActionReferencePanel = {
  fields?: any
  sys?: {
    contentType?: {
      sys?: {
        id?: string
      }
    }
  }
}

export type ActionFields = {
  fields?: (ButtonProps | AnchorProps) & {
    type: Type
    text: string
    url: string
    icon?: IconFields
    quickLinkPicture?: PictureFields
    supportingData?: {
      home?: {
        action?: {
          value: ActionType /* Action can come directly from Prop or from Contentful field under supportingData */
        }
        clickOperation?: {
          value: 'popup' | 'slide'
        }
        newTab?: { value: boolean }
        variant?: { value: Variants }
      }
    }
    personalizationTags?: PersonalizationTags
    references?: ActionReferencePanel[]
  }
  references?: ActionReferencePanel[]
  sys?: {
    id?: string
    contentType?: {
      sys?: {
        type: string
      }
    }
    locale?: CntflLocales
  }
}

export type ActionProps = ActionFields & {
  action?: ActionType /* Action can come directly from Prop or from Contentful field under supportingData */
  button?: ButtonProps | AnchorProps /* Override button options when used as directly, instead as a Contentful field */
  sfccPromotion?: boolean
  productId?: string
  gaOptions?: DataLayerOptional
}

const props = defineProps<ActionProps>()
const attrs = useAttrs()

const { type, ...actionButtonProps } = actionTransformer(props)
const actionProps = { ...actionButtonProps, ...props.button, ...attrs }

const { variant, action = props.action, innerPanel, isProductList, isRTPModal } = actionModalTransformer(props)

const { dispatch, addToBagLoading } = useContentActions()
</script>

<template>
  <span class="action-wrapper" v-if="personalizationControl(fields?.personalizationTags)">
    <Button
      v-if="action === 'mhj-login-panel'"
      v-bind="actionProps"
      class="action"
      @click="dispatch('mhj-login-panel')"
    >
      <slot />
    </Button>

    <Button
      v-else-if="action === 'mhj-signup-panel'"
      data-testid="action-signup-panel"
      v-bind="actionProps"
      class="action"
      @click="dispatch('mhj-signup-panel')"
    >
      <slot />
    </Button>

    <Button v-else-if="action === 'setMyStore'" v-bind="actionProps" class="action" @click="dispatch('setMyStore')">
      <slot />
    </Button>

    <Button
      v-else-if="action === 'addToBag'"
      v-bind="actionProps"
      class="action"
      @click="dispatch('addToBag', { productId })"
      :disabled="addToBagLoading"
    >
      <slot />
    </Button>

    <div v-else-if="sfccPromotion" class="action">
      <slot :performAction="dispatch" />
    </div>

    <RTPModal v-else-if="isRTPModal" :button="actionProps" :modal-variant="variant" :modal-content="innerPanel?.fields">
      <slot />
    </RTPModal>
    <ProductListModal
      v-else-if="isProductList"
      :button="actionProps"
      :modal-variant="variant"
      :modal-content="innerPanel?.fields"
    >
      <slot />
    </ProductListModal>
    <Button v-else-if="type === 'Button'" v-bind="actionProps" class="action"><slot /></Button>
    <Anchor v-else v-bind="actionProps" class="action"><slot /></Anchor>
  </span>
</template>
